import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Text, TextButton } from '@oneaudi/unified-web-components';
import { useI18n } from '@oneaudi/i18n-context';
import { SmallFiveStarRating } from '../../common/FiveStarRating/SmallFiveStarRating';
import { DealerReview } from '../../../types';
import { DealerResponse } from './DealerResponse';
import { messages } from '../../../i18n/messages';

const CHAR_CUTOFF = 160;

const CustomerReviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 12px;
`;

const HeaderRowContainer = styled.div`
  display: grid;
  grid-template-columns: max-content min-content;
  justify-content: space-between;
  align-items: center;
  row-gap: 8px;
  color: #333333;
`;

const PartialReviewStyled = styled(Text)<{ isTextCutOff: boolean }>`
  color: #000000;
  display: ${(props) => (props.isTextCutOff ? 'block' : 'none')};
`;

const FullReviewStyled = styled(Text)<{ isTextCutOff: boolean }>`
  color: #000000;
  display: ${(props) => (props.isTextCutOff ? 'none' : 'block')};
`;

const ReadMoreButtonStyled = styled<typeof TextButton>(TextButton)`
  width: auto;
  height: 20px;
  align-items: end;
  color: #000000;
  text-decoration: underline;
  min-width: auto;
  border: none;
  padding: 0;

  svg {
    display: none;
  }
`;

const DateTextStyled = styled(Text)`
  word-break: keep-all;
`;

interface CustomerReviewProps {
  review: DealerReview;
}

export const CustomerReview = ({ review }: CustomerReviewProps) => {
  const [isTextCutOff, setIsTextCutOff] = useState(false);
  const onReviewButtonClick = () => {
    setIsTextCutOff(false);
  };
  useEffect(() => {
    if (review.text.length > CHAR_CUTOFF) {
      setIsTextCutOff(true);
    }
  }, []);
  const readMoreLabel = useI18n(messages.five_star_read_more);
  const readMoreButton = (() => {
    return (
      <>
        ...&nbsp;
        <ReadMoreButtonStyled variant="tertiary" onClick={onReviewButtonClick}>
          {readMoreLabel}
        </ReadMoreButtonStyled>
      </>
    );
  })();
  const partialReview = (() => {
    if (isTextCutOff) {
      const cutReview = review.text.slice(0, CHAR_CUTOFF);
      if (review.text[CHAR_CUTOFF + 1] === ' ') {
        return cutReview;
      }
      const lastWhiteSpaceIndex = cutReview.lastIndexOf(' ');
      return cutReview.slice(0, lastWhiteSpaceIndex);
    }
    return '';
  })();
  const categoryText =
    (review.categoryKey === 'SALES' && useI18n(messages.five_star_sales_category)) ||
    useI18n(messages.five_star_service_category);
  return (
    <CustomerReviewContainer>
      <HeaderRowContainer>
        <Text variant={{ order: '1', style: 'bold', type: 'copy' }}>{categoryText}</Text>
        <DateTextStyled variant={{ order: '2', style: 'bold', type: 'copy' }}>
          {review.date.toLocaleDateString()}
        </DateTextStyled>
        <SmallFiveStarRating rating={+review.rating} />
      </HeaderRowContainer>
      <PartialReviewStyled
        variant={{ order: '1', style: 'normal', type: 'copy' }}
        isTextCutOff={isTextCutOff}
      >
        {partialReview}
        {readMoreButton}
      </PartialReviewStyled>
      <FullReviewStyled
        variant={{ order: '1', style: 'normal', type: 'copy' }}
        isTextCutOff={isTextCutOff}
      >
        {review.text}
      </FullReviewStyled>
      {review.dealerResponse && <DealerResponse {...review.dealerResponse} />}
    </CustomerReviewContainer>
  );
};
